import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  Grid,
  TextField,
  List,
  ListItemText,
  DialogProps,
  makeStyles,
  Button,
} from "@material-ui/core";

import {
  PagedResponse,
  PagedResponseEmpty,
  Model,
  QueryParams,
} from "@ofqual/portal-core/core/types";
import { SearchDialogPager } from "./SearchDialogPager";
import { useIsMounted } from "@ofqual/portal-core/core/hooks";
import { Skeleton } from "@material-ui/lab";
import { useDebouncedCallback } from "use-debounce";
import { useAsync } from "react-async-hook";

const ID_FIELD_NAME = "id";
const LABEL_FIELD_NAME = "label";
const DISABLED_FIELD_NAME = "disabled";
const DEFAULT_DEBAOUNCE_MS = 750;

type SearchDialogProps = {
  label: React.ReactNode;
  dialogOpenProps: { isDialogOpen: boolean; initialSearchText: string };
  service: {
    search: (params?: QueryParams) => Promise<PagedResponse<Model>>;
  };
  addservice: {
    add: (keyword: string, params?: QueryParams) => Promise<Model>;
  };
  onSelectOrClose: (val?: {
    id: any;
    label: string;
    [key: string]: any;
  }) => void;
  selectedItems?: { id: string | number }[];
  titleCustomElement?: JSX.Element;
  datatest?: string;
  dialogWidth?: DialogProps["maxWidth"];
  debounceMs?: number;
};

type SearchInput = {
  service: {
    search: (params?: QueryParams | undefined) => Promise<PagedResponse<Model>>;
  };
  requestedSearchText: string;
  requestedPage: number;
};

type AddInput = {
  service: {
    add: (keyword: string, params?: QueryParams | undefined) => Promise<Model>;
  };
  keyword: string;
};

type SearchFn = (input: SearchInput) => Promise<PagedResponse<Model>>;

type AddFn = (input: AddInput) => Promise<Model>;

const fetchSearchResults: SearchFn = async ({
  service,
  requestedSearchText,
  requestedPage,
}) => {
  return await service.search({
    query: requestedSearchText,
    page: requestedPage,
  });
};

const addItemResults: AddFn = async ({ service, keyword }) => {
  return await service.add(keyword);
};

const useStyles = makeStyles(() => ({
  dialogAction: { justifyContent: "space-between" },
  dialogBtn: { paddingLeft: "20px" },
}));

export const SearchDialog: React.FC<SearchDialogProps> = ({
  label,
  dialogOpenProps: { isDialogOpen, initialSearchText },
  service,
  addservice,
  onSelectOrClose,
  selectedItems,
  datatest,
  dialogWidth = "sm",
  debounceMs = DEFAULT_DEBAOUNCE_MS,
}) => {
  const classes = useStyles();
  // const [isLoading, setIsLoading] = useState(false);
  // const [isLoaded, setIsLoaded] = useState(false);
  const scrollableContentRef = useRef<HTMLElement>();

  // const [apiResponse, setApiResponse] = useState<PagedResponse<Model>>(
  //   PagedResponseEmpty
  // );
  //const isLoaded = !isPagedResponseEmpty(apiResponse);

  const [searchText, setSearchText] = useState(initialSearchText);

  const [requestedSearchText, setRequestedSearchText] = useState("");
  const [requestedPage, setRequestedPage] = useState<number>(0);

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!searchText) {
      if (isMountedRef.current) {
        setSearchText(initialSearchText);
      }
    }
  }, [initialSearchText, searchText, isMountedRef]);

  const asyncResults = useAsync(async () => {
    let resp: PagedResponse<Model> = PagedResponseEmpty;
    try {
      resp = await fetchSearchResults({
        service,
        requestedSearchText,
        requestedPage,
      });
      if (scrollableContentRef && scrollableContentRef.current) {
        scrollableContentRef.current.scrollTo(0, 0);
      }
    } catch (err: any) {
      if (
        (typeof err.status === "number" && err.status === 401) ||
        err.status === 403
      ) {
        resp = {
          data: [
            {
              id: -1,
              label:
                "Your user account does not have permissions to search for or retrieve this kind of record.",
              [DISABLED_FIELD_NAME]: true,
            },
          ],
          count: -1,
          page: requestedPage,
          pageSize: 0,
        };
      } else {
        throw err;
      }
    }

    resp.data = resp.data.map((item) => ({
      ...item,
      [DISABLED_FIELD_NAME]:
        item[DISABLED_FIELD_NAME] ||
        selectedItems?.some((selectedItem) => selectedItem.id === item.id),
    }));

    if (scrollableContentRef && scrollableContentRef.current) {
      scrollableContentRef.current.scrollTo(0, 0);
    }

    return resp;
  }, [service, requestedSearchText, requestedPage]);

  const handleClose = (val?: {
    id: any;
    label: string;
    [key: string]: any;
  }) => {
    setSearchText("");
    setRequestedSearchText("");
    setRequestedPage(0);
    onSelectOrClose(val);
  };

  const addItem = async (keyword: string) => {
    if (keyword !== "") {
      let res: Model;
      try {
        res = await addItemResults({
          service: addservice,
          keyword,
        });

        // if (res.status === 200 || res.status === 204) {
        handleClose({
          ...res.data,
          id: res.data[ID_FIELD_NAME],
          label: res.data[LABEL_FIELD_NAME],
        });
        // }
      } catch (err: any) {
        if (
          (typeof err.status === "number" && err.status === 401) ||
          err.status === 403
        ) {
          alert("Your account does not have permissions to add a keyword");
        } else {
          throw err;
        }
      }
    }
  };

  const handleKeyUp = () => {
    setRequestedPage(0);
    setRequestedSearchText(searchText);
  };

  const _onKeyUp = useDebouncedCallback(handleKeyUp, debounceMs);

  return (
    <Dialog
      data-test={`dialog-${datatest}`}
      open={isDialogOpen}
      onClose={() => handleClose()}
      maxWidth={dialogWidth}
      fullWidth
      scroll="paper"
      PaperProps={{
        style: {
          minHeight: "80vh", // stop the dialog resizing as list grows/shrinks
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle>
        <TextField
          data-test="search-dialog-input"
          name="query"
          label={"Search for " + label + "..."}
          fullWidth
          autoFocus
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyUp={() => {
            _onKeyUp.callback();
          }}
        />
      </DialogTitle>

      <DialogContent ref={scrollableContentRef}>
        <Grid container spacing={3}>
          <Grid item sm={12}></Grid>
          <Grid item sm={12}>
            <div style={{ minHeight: "100vh" }}>
              {!asyncResults.loading ? (
                <List data-test={`list-${datatest}`}>
                  {asyncResults.result?.data.map((item) => {
                    return (
                      <ListItem
                        key={item[ID_FIELD_NAME]}
                        button
                        onClick={() => {
                          handleClose({
                            ...item,
                            id: item[ID_FIELD_NAME],
                            label: item[LABEL_FIELD_NAME],
                          })
                        }
                        }
                        data-value={item[ID_FIELD_NAME]}
                      // disabled={!!item[DISABLED_FIELD_NAME]}
                      >
                        <ListItemText primary={item[LABEL_FIELD_NAME]} />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <div>
                  <Skeleton variant="text" height={60} />
                  <Skeleton variant="text" height={60} />
                  <Skeleton variant="text" height={60} />
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={searchText !== "" ? classes.dialogAction : ""}>
        {searchText !== "" && (
          <div className={classes.dialogBtn}>
            <Button
              color="primary"
              onClick={() => addItem(searchText)}
              data-test="add-keyword"
            >
              Add a keyword
            </Button>
          </div>
        )}
        <div className="SearchListFooter">
          {asyncResults.result?.count !== -1 && (
            <SearchDialogPager
              isLoading={asyncResults.loading}
              apiResponse={asyncResults.result || PagedResponseEmpty}
              setRequestedPage={setRequestedPage}
            />
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
};
